import { Messages } from 'primereact/messages';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage';
import { getErrorMessage } from '../../../utils/errors';
import { ErrorMessage } from '../../components/ErrorMessage';
import { getApiClient } from '../../../api';
import { formatDate } from '../../utils/format';
import { showErrorMessage } from '../../utils/messages';
import { scrollTop } from '../../utils/scroll';
import { MeetingAddForm } from './components/MeetingAddForm';
import { MeetingBreadCrumb } from './components/MeetingBreadCrumb';
import { DateOverlapDialog } from './components/DateOverlapDialog';

export default function MeetingAdd() {
    const { municipalityId, bodyType, termId } = useParams();
    const currentLocale = 'cs';

    const navigate = useNavigate();

    const msgs = useRef(null);

    const [loading, setLoading] = useState(true);
    const [initError, setInitError] = useState(null);
    const [politicalEntities, setPoliticalEntities] = useState([]);

    const [pendingFormData, setPendingFormData] = useState(null);
    const [overlaps, setOverlaps] = useState(null);
    const [isVisibleDateOverlapDialog, setIsVisibleOverlapDialog] = useState(false);
    const refDateOverlapDialogMsgs = useRef(null);

    const [attendanceOptionsList, setAttendanceOptionsList] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [politicalEntitiesResponse, attendanceOptionsResponse] = await Promise.all([
                getApiClient().getAdminPoliticalEntityByMunicipalityIdAndBody(municipalityId, bodyType),
                getApiClient().getAttendanceOptionsLanguageListByLang(currentLocale)
            ]);

            setPoliticalEntities(politicalEntitiesResponse);
            setAttendanceOptionsList(attendanceOptionsResponse);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setInitError(errorMessage ?? 'Nepodařilo se načíst politické subjekty');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [currentLocale, municipalityId]);

    const forceSubmit = async (data) => {
        await onSubmit({ ...data, _force: true });
    };

    const onSubmit = async (data) => {
        const force = data._force || false;

        const requestData = {
            date_from: formatDate(data.date_from),
            date_to: data.date_from.getTime() === data.date_to?.getTime() ? null : formatDate(data.date_to),
            number: data.number,
            recording_url: data.recording_url,
            members: [],
            members_add: [],
            members_remove: [],
            force
        };
        data.members.forEach((member) => {
            if (member.delete) {
                requestData.members_remove.push(member.member_id);
                return;
            }

            const memberData = {
                id: member.member_id,
                political_entity: member.political_entity,
                attendance: member.attendance
            };

            if (member.add) {
                requestData.members_add.push(memberData);
            } else {
                requestData.members.push(memberData);
            }
        });
        try {
            const body = await getApiClient().postAdminMeetingByMunicipalityIdAndBodyAndTermId(municipalityId, bodyType, Number(termId), requestData);
            navigate(
                `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani/${body.id}/hlasovani`
            );
        } catch (error) {
            if (error.response?.status === 409) {
                setPendingFormData(data);
                showDateOverlapDialog(error.response?.data);
            } else {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se přidat nové zasedání', { replace: true });
                scrollTop();
            }
        }
    };

    const showDateOverlapDialog = async (data) => {
        setOverlaps(data.overlaps);
        setIsVisibleOverlapDialog(true);
    }

    const hideDateOverlapDialog = () => {
        setOverlaps(null);
        setIsVisibleOverlapDialog(false);
    };

    const handleDateOverlap = async () => {
        if (!pendingFormData) return;

        await forceSubmit(pendingFormData);
        setPendingFormData(null);
        hideDateOverlapDialog();
    }

    const onMemberFetchError = (errorMessage) => {
        showErrorMessage(msgs, errorMessage, { replace: true });
    };

    return loading ? (
        <LoadingPage />
    ) : (
        <>
            <MeetingBreadCrumb
                extraItems={[
                    {
                        label: 'Přidat',
                        url: `/admin/municipality/${municipalityId}/${bodyType}/funkcni-obdobi/${termId}/zasedani/new`
                    }
                ]}
            />
            <h1>Přidat zasedání</h1>

            {initError ? (
                <ErrorMessage prefix='Nelze použít formulář' text={initError} />
            ) : (
                <>
                    <Messages ref={msgs} className='page__messages' />
                    <MeetingAddForm
                        politicalEntities={politicalEntities}
                        attendanceOptionsList={attendanceOptionsList}
                        onSubmit={onSubmit}
                        onMemberFetchError={onMemberFetchError}
                    />

                    <DateOverlapDialog
                        overlaps={overlaps}
                        isVisible={isVisibleDateOverlapDialog}
                        onHide={hideDateOverlapDialog}
                        onClickConfirm={handleDateOverlap}
                        msgs={refDateOverlapDialogMsgs}
                    />
                </>
            )}
        </>
    );
}
